import { useMemo, useEffect, useState, useRef } from 'react';
import { useVenues } from '../organization/useVenues';
import { sessionSubject } from '../../state/sessionObservable';
import { getSession } from '../../state/preferences';

export enum SubNavigatorName {
  Login = 'Login',
  Pairing = 'Pairing',
  Cart = 'CartNavigator',
  Idle = 'LoginIdle',
}

// Check session valid by making a request
// if there is no token or valid session: UNAUTHENTICATE error will throw
// if there is valid token: the request success and redirect user to cart navigator
// if there is expired token but a valid refresh token: the request success and redirect user to cart navigator
// if there is expired both token and refresh token: UNAUTHENTICATE error will throw
export const useInitialScreen = () => {
  const { loading, error } = useVenues();
  const [isValidSession, setValidSession] = useState(false);
  const [isSessionLoading, setSessionLoading] = useState(true);

  // Track the last valid screen to prevent resetting during loading transitions
  const lastValidScreenRef = useRef<SubNavigatorName>(SubNavigatorName.Idle);

  useEffect(() => {
    setSessionLoading(true);
    getSession().then(session => {
      if (session) {
        sessionSubject.next(session);
        // A paired device with no organization id can happen when the
        // account's ID token has no default org (e.g. multi-org accounts) —
        // route back through AssignPos so the org gets (re)selected instead
        // of getting stuck sending requests with a missing organizationId.
        if (!session.customerDisplay || !session.currentOrganization?.id) {
          setValidSession(false);
        } else {
          setValidSession(true);
        }
        setSessionLoading(false);
      }
    });
  }, []);

  const screen = useMemo(() => {
    if (loading || isSessionLoading) {
      // While loading, preserve and return the last non-Idle screen to avoid
      // unmounting and losing component state. If the last recorded screen is
      // Idle (e.g., on initial load), explicitly return Idle.
      return lastValidScreenRef.current !== SubNavigatorName.Idle
        ? lastValidScreenRef.current
        : SubNavigatorName.Idle;
    }

    if (error) return SubNavigatorName.Login;
    if (!isValidSession) return SubNavigatorName.Pairing;
    return SubNavigatorName.Cart;
  }, [loading, error, isValidSession, isSessionLoading]);

  // Update last valid screen when screen changes (side effect separated from useMemo)
  useEffect(() => {
    if (screen !== SubNavigatorName.Idle) {
      lastValidScreenRef.current = screen;
    }
  }, [screen]);

  return {
    screen,
  };
};
